// App-wide owner of the PWA install state: the platform, whether the app is
// already installed, and the single-use `beforeinstallprompt` event.
//
// The browser fires `beforeinstallprompt` once, early, and not again — often
// before the screen that wants it has mounted. So the listener lives here, is
// started before the first render (`initPwaInstall()` in main.tsx), and every
// consumer (`usePwaInstall`) reads the held event instead of listening itself.
// See [[install-app-banner.spec]] § One owner for the install event.

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: InstallOutcome }>;
}

export type InstallOutcome = "accepted" | "dismissed";

export type PwaPlatform =
  | "chromium"
  | "ios-safari"
  | "ios-other"
  | "desktop-safari"
  | "unsupported";

export interface PwaInstallState {
  platform: PwaPlatform;
  /** Running as an installed app right now (display-mode, or iOS standalone). */
  isStandalone: boolean;
  /** The held `beforeinstallprompt` event; null until one fires, and after use. */
  installEvent: BeforeInstallPromptEvent | null;
  /**
   * Permanent, device-level: the app was installed (`appinstalled`) or the
   * install prompt was accepted. Read by the install banner only.
   */
  installed: boolean;
}

/** Device-level flag, like the theme: survives reloads and sign-out. */
export const PWA_INSTALLED_KEY = "otus:pwa-installed";

function detectPlatform(): PwaPlatform {
  if (typeof navigator === "undefined") return "unsupported";
  const ua = navigator.userAgent;

  const isIos = /iPhone|iPad|iPod/.test(ua);
  if (isIos) {
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isSafari ? "ios-safari" : "ios-other";
  }

  if ("BeforeInstallPromptEvent" in window) return "chromium";

  const isMac = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium/.test(ua);
  if (isMac && isSafari) return "desktop-safari";

  return "unsupported";
}

function getIsStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    ("standalone" in navigator &&
      (navigator as never as { standalone: boolean }).standalone)
  );
}

function readInstalledFlag(): boolean {
  try {
    return window.localStorage.getItem(PWA_INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeInstalledFlag(): void {
  try {
    window.localStorage.setItem(PWA_INSTALLED_KEY, "1");
  } catch {
    // Storage blocked: the in-memory flag still holds for this session.
  }
}

let state: PwaInstallState | null = null;
let started = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Seeded on first read; `platform` and `isStandalone` do not change mid-session. */
export function getPwaInstallState(): PwaInstallState {
  state ??= {
    platform: detectPlatform(),
    isStandalone: getIsStandalone(),
    installEvent: null,
    installed: readInstalledFlag(),
  };
  return state;
}

function setState(patch: Partial<PwaInstallState>) {
  state = { ...getPwaInstallState(), ...patch };
  emit();
}

export function subscribePwaInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function onBeforeInstallPrompt(e: Event) {
  // Suppress the browser's own mini-infobar: the app's banner and Settings
  // section are the install surfaces.
  e.preventDefault();
  setState({ installEvent: e as BeforeInstallPromptEvent });
}

function onAppInstalled() {
  writeInstalledFlag();
  setState({ installEvent: null, installed: true });
}

/**
 * Starts listening for `beforeinstallprompt` (Chromium) and `appinstalled`.
 * Idempotent; call once before the first render.
 */
export function initPwaInstall(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  if (getPwaInstallState().platform === "chromium") {
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }
  window.addEventListener("appinstalled", onAppInstalled);
}

/**
 * Shows the native install prompt from the held event. Returns the user's
 * choice, or null when no event is held. The event is single-use, so it is
 * dropped whatever happens, including a rejected `prompt()`. Accepting records
 * the permanent installed flag.
 */
export async function promptInstall(): Promise<InstallOutcome | null> {
  const event = getPwaInstallState().installEvent;
  if (!event) return null;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") {
      writeInstalledFlag();
      setState({ installed: true });
    }
    return outcome;
  } finally {
    if (getPwaInstallState().installEvent === event) {
      setState({ installEvent: null });
    }
  }
}

/** Tears the store down so a test can re-seed it. Not for app code. */
export function resetPwaInstallStore(): void {
  if (typeof window !== "undefined") {
    window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", onAppInstalled);
  }
  state = null;
  started = false;
  listeners.clear();
}
